import React, { useState, useEffect, useRef } from 'react';
import './App.css';

/* ─────────────────────────────────────────────
   DATA
───────────────────────────────────────────── */

const PROJECTS = [
  {
    id: '01',
    title: 'Akasa Air Email Intent Identifier',
    tag: 'NLP · LLM · Analytics',
    color: '#00d4ff',
    short: 'LLM-based email classification system for airline operations.',
    detail: `Built an intelligent email triage system for Akasa Air using Large Language Models. The system classifies incoming emails by intent (booking, complaint, query, escalation) and detects sensitive language such as threats, abusive content, or mentions of officials. Emails are automatically routed to the appropriate team mailbox based on priority.

Key highlights:
• LLM-based multi-class intent classification pipeline
• Sensitive-word detection with configurable rule engine
• Auto-routing to team mailboxes with priority scoring
• Analytics dashboard for operational insights — volumes, response SLA, category trends
• Integrated with existing enquiry and logging systems

Stack: Python, FastAPI, LLMs, REST APIs, Analytics Dashboard`,
  },
  {
    id: '02',
    title: 'Ducati WhatsApp AI Bot',
    tag: 'RAG · Tool Calling · WhatsApp',
    color: '#ff6b35',
    short: 'Conversational AI bot for bike discovery and test ride bookings.',
    detail: `Developed a WhatsApp-based conversational AI assistant for Ducati India. The bot uses Retrieval-Augmented Generation (RAG) combined with tool-calling to answer customer queries about bikes with rich, accurate information.

Key highlights:
• RAG pipeline over Ducati's product catalog for grounded responses
• Tool calling to fetch real-time bike specs, media (images/videos), pricing
• Personalised recommendation engine based on user preferences (budget, use case, style)
• End-to-end test ride booking via chat — captures user details, preferred dealership & date
• Seamless handoff to human agent when needed

Stack: Python, LLMs, RAG, WhatsApp Business API, Vector DB`,
  },
  {
    id: '03',
    title: 'Bose Product Support Voice Bot',
    tag: 'Voice AI · Local LLM · Redis',
    color: '#00ffaa',
    short: 'On-device voice assistant for customer-specific troubleshooting.',
    detail: `Built a product support voice bot for Bose using local LLMs to ensure data privacy and low latency. The bot handles real-time voice interactions and provides grounded troubleshooting steps specific to the customer's product and issue.

Key highlights:
• Local LLM deployment (no data leaves the premises) using Chrome speech stack
• Redis for session state and conversation memory across turns
• Customer-specific context injection — product model, purchase date, past issues
• Grounded troubleshooting with step-by-step guidance
• Escalation path to live support when bot confidence is low

Stack: Python, Local LLMs, Chrome Speech API, Redis, FastAPI`,
  },
  {
    id: '04',
    title: 'Real-Time Noise Cancellation App',
    tag: 'Deep Learning · Audio · Production',
    color: '#a78bfa',
    short: '90–95% background noise reduction for BPO agents, deployed to production.',
    detail: `Developed a real-time audio noise cancellation application for BPO agent support. The system runs on-device deep learning models to strip background noise from live audio streams with sub-100ms latency.

Key highlights:
• On-device deep learning model for real-time noise suppression
• Low-latency audio streaming pipeline achieving 90–95% noise reduction
• Deployed to production for BPO environments — tested with 100+ concurrent agents
• Optimised model inference for CPU-bound edge devices
• Monitoring dashboard for audio quality metrics

Stack: Python, Deep Learning (PyTorch), Audio DSP, Streaming APIs`,
  },
  {
    id: '05',
    title: 'Conversational AI Platform (Qubrid)',
    tag: 'STT · TTS · VAD · LLM',
    color: '#fbbf24',
    short: 'Full-stack conversational AI combining speech and language models.',
    detail: `At Qubrid AI, designed and built a full conversational AI system that seamlessly integrates speech and language pipelines for natural voice-based interactions.

Key highlights:
• Speech-to-Text (STT) for accurate transcription across accents
• Voice Activity Detection (VAD) to handle interruptions and pauses naturally
• LLM core for intent understanding and response generation
• Text-to-Speech (TTS) with natural prosody
• Modular architecture — each component independently upgradeable
• Deployed and hosted on Google Cloud Run via Docker

Stack: Python, STT/TTS engines, VAD, LLMs, Docker, Google Cloud Run`,
  },
  {
    id: '06',
    title: 'Hindi Grammar Error Correction',
    tag: 'NLP · Fine-Tuning · mT5',
    color: '#f472b6',
    short: 'Fine-tuned mT5 for Hindi GEC achieving 92.34% precision.',
    detail: `Fine-tuned Google's multilingual mT5 model specifically for Grammar Error Correction (GEC) of Hindi sentences — a low-resource NLP task with limited publicly available datasets.

Key highlights:
• Custom Hindi GEC dataset curation and cleaning pipeline
• Fine-tuned mT5-base using sequence-to-sequence training
• Achieved 92.34% precision on held-out test set
• Handles common Hindi grammatical errors: case markers, verb agreement, postpositions
• Published learnings internally as a research reference

Stack: Python, PyTorch, HuggingFace Transformers, mT5, Google Colab`,
  },
];

const SKILLS = [
  { cat: 'AI / ML', items: ['Large Language Models', 'Generative AI', 'Deep Learning', 'RAG', 'NLP', 'Fine-Tuning', 'Multi-Agent Systems'] },
  { cat: 'Frameworks', items: ['PyTorch', 'TensorFlow', 'HuggingFace', 'FastAPI', 'Flask', 'Django'] },
  { cat: 'Infrastructure', items: ['Docker', 'Google Cloud Run', 'AWS', 'vLLM', 'TensorRT', 'Redis', 'Vector DBs'] },
  { cat: 'Languages & Tools', items: ['Python', 'Linux', 'GitHub', 'Bitbucket', 'Milvus'] },
];

const EXPERIENCE = [
  {
    company: 'NeoSoft Technologies',
    role: 'AI Engineer',
    period: 'Mar 2025 – Present',
    note: 'CMMI DEV/5 · Star of the Month',
    points: [
      'Akasa Air Email Intent Identifier — LLM classification + sensitive-word detection + auto-routing',
      'Ducati WhatsApp bot — RAG + tool-calling for bike specs, recommendations & test ride booking',
      'Bose Voice Support Bot — local LLMs, Chrome speech stack, Redis for grounded troubleshooting',
      'Scalable local LLM deployment using vLLM + OpenAI-compatible API',
      'Real-time noise cancellation app — 90–95% noise reduction, deployed to BPO production',
      'Airline pet-assistance chatbot — policy queries, pet detail capture, in-flight guidance',
    ],
  },
  {
    company: 'Qubrid AI',
    role: 'AI Engineer',
    period: 'Feb 2024 – Feb 2025',
    note: '1 year',
    points: [
      'No-code LLM & Stable Diffusion fine-tuning POC for rapid deployment platform',
      'Full conversational AI system — STT, TTS, VAD and LLM integration',
      'Company chatbot with RAG, hosted on Google Cloud Run + Milvus vector DB',
      'Custom chatbot builder for Telegram & Discord with optimised response efficiency',
      'AI model deployment on GCP & AWS',
      'TensorRT research + whitepapers on fine-tuning and quantization impact on LLMs',
    ],
  },
  {
    company: 'Koderbox Technologies',
    role: 'AI Engineer → Intern',
    period: 'Oct 2022 – Feb 2024',
    note: '1.6 years',
    points: [
      'Fine-tuned mT5 for Hindi Grammar Error Correction — 92.34% precision',
      'Manga panel extraction pipeline for dataset diversity enhancement',
      'Fine-tuned Stable Diffusion for high-quality image generation',
      'Text-to-Image model for Manga-style image generation',
      'Hindi textual data extraction from global news articles',
    ],
  },
];

/* ─────────────────────────────────────────────
   NAVBAR
───────────────────────────────────────────── */
function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', h);
    return () => window.removeEventListener('scroll', h);
  }, []);
  const links = ['Home', 'About', 'Experience', 'Projects', 'Skills', 'Contact'];
  return (
    <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
      <div className="nav-brand">
        <span className="brand-bracket">[</span>
        <span className="brand-name">PK</span>
        <span className="brand-bracket">]</span>
      </div>
      <ul className={`nav-links ${open ? 'open' : ''}`}>
        {links.map(l => (
          <li key={l}><a href={`#${l.toLowerCase()}`} onClick={() => setOpen(false)}>{l}</a></li>
        ))}
      </ul>
      <button className="hamburger" onClick={() => setOpen(o => !o)} aria-label="menu">
        <span /><span /><span />
      </button>
    </nav>
  );
}

/* ─────────────────────────────────────────────
   HERO
───────────────────────────────────────────── */
function Hero() {
  const [typed, setTyped] = useState('');
  const phrases = ['AI Engineer', 'LLM Specialist', 'Voice AI Builder', 'RAG Architect'];
  const [pi, setPi] = useState(0);
  useEffect(() => {
  let i = 0;
  let deleting = false;
  let timeout;

  const tick = () => {
    const phrase = phrases[pi % phrases.length];

    if (!deleting) {
      // typing
      i++;
      setTyped(phrase.slice(0, i));

      // speed control while typing
      let speed = 90; // base speed (medium)

      if (i > phrase.length * 0.7) speed = 140; // slow at end

      if (i === phrase.length) {
        deleting = true;
        timeout = setTimeout(tick, 800); // pause when full word typed
        return;
      }

      timeout = setTimeout(tick, speed);
    } else {
      // deleting
      i--;
      setTyped(phrase.slice(0, i));

      let speed = 40; // fast delete

      if (i === 0) {
        deleting = false;
        setPi(p => p + 1);
        timeout = setTimeout(tick, 200); // quick switch
        return;
      }

      timeout = setTimeout(tick, speed);
    }
  };

  timeout = setTimeout(tick, 500);

  return () => clearTimeout(timeout);
}, [pi]);
  // useEffect(() => {
  //   let i = 0; let deleting = false; let cur = '';
  //   const tick = () => {
  //     const phrase = phrases[pi % phrases.length];
  //     if (!deleting) { cur = phrase.slice(0, i + 1); i++; if (i === phrase.length) { deleting = true; setTimeout(tick, 1800); return; } }
  //     else { cur = phrase.slice(0, i - 1); i; if (i === 0) { deleting = false; setPi(p => p + 1); } }
  //     setTyped(cur);
  //     setTimeout(tick, deleting ? 75 : 130);
  //   };
  //   const t = setTimeout(tick, 1000);
  //   return () => clearTimeout(t);
  // }, [pi]);  // eslint-disable-line

  return (
    <section id="home" className="hero">
      <div className="hero-grid-bg" />
      <div className="hero-glow" />
      <div className="hero-inner">
        <p className="label">// AI Engineer · Mumbai, India</p>
        <h1 className="hero-name">Puru <span className="accent-name">Koli</span></h1>
        <div className="hero-typewriter">
          <span className="tw-prefix">&gt; </span>
          <span className="tw-text">{typed}</span>
          <span className="tw-cursor">|</span>
        </div>
        <p className="hero-bio">
          Building intelligent systems — from LLM pipelines and RAG architectures
          to voice bots and real-time audio AI. Currently pushing towards Physical AI.
        </p>
        <div className="hero-cta">
          <a href="#projects" className="btn btn-primary">View Projects</a>
          <a href="#contact" className="btn btn-outline">Hire Me</a>
        </div>
        <div className="hero-chips">
          {['LLMs', 'RAG', 'Voice AI', 'Fine-Tuning', 'Multi-Agent', 'Docker', 'GCP'].map(t => (
            <span key={t} className="chip">{t}</span>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   ABOUT
───────────────────────────────────────────── */
function About() {
  return (
    <section id="about" className="about section">
      <div className="container">
        <div className="about-grid">
          <div>
            <p className="label">// About Me</p>
            <h2 className="section-title">AI Engineer<br />with <em>depth.</em></h2>
            <p className="about-para">
              I'm Puru Koli, an AI Engineer with hands-on experience across the full
              ML lifecycle — from data preparation and model fine-tuning to scalable
              cloud deployment and production monitoring.
            </p>
            <p className="about-para">
              I've built AI products used in production at airlines, motorcycle brands,
              and audio companies. My work spans conversational AI, NLP, voice systems,
              and real-time deep learning applications.
            </p>
            <p className="about-para">
              My next frontier is <strong>Physical AI</strong> — bridging intelligent
              software with the real, physical world.
            </p>
          </div>
          <div className="about-right">
            <div className="edu-card">
              <p className="label">// Education</p>
              {[
                ['B.E. Mechanical Engineering', 'Saraswati College of Engineering', '2022', '81.62%'],
                ['Diploma — Mechanical Engg.', 'Saraswati Institute of Technology', '2019', '76.29%'],
                ['SSC', 'St. George School, New Panvel', '2016', '80.20%'],
              ].map(([deg, inst, yr, pct]) => (
                <div className="edu-item" key={yr}>
                  <div className="edu-deg">{deg}</div>
                  <div className="edu-inst">{inst} · {yr}</div>
                  <div className="edu-pct">{pct}</div>
                </div>
              ))}
            </div>
            <div className="cert-card">
              <p className="label">// Certifications</p>
              {[
                ['Intro to Large Language Models', 'Google Cloud · Jan 2024'],
                ['Generative AI Fundamentals', 'Google Cloud · Jan 2024'],
                ['Machine Learning', 'Simplilearn · Aug 2022'],
              ].map(([c, i]) => (
                <div className="cert-item" key={c}>
                  <span className="cert-dot" />
                  <div>
                    <div className="cert-name">{c}</div>
                    <div className="cert-by">{i}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   EXPERIENCE — sticky overlapping scroll cards
───────────────────────────────────────────── */
function Experience() {
  return (
    <section id="experience" className="experience section">
      <div className="container">
        <p className="label">// Work History</p>
        <h2 className="section-title">Where I've<br /><em>worked.</em></h2>
      </div>
      <div className="stack-track">
        {EXPERIENCE.map((exp, i) => (
          <div className="stack-pin" key={i} style={{ '--idx': i, '--total': EXPERIENCE.length }}>
            <div className="container">
              <div className="timeline-card stack-card">
                <div className="tl-header">
                  <div>
                    <div className="tl-company">{exp.company}</div>
                    <div className="tl-role">{exp.role}</div>
                  </div>
                  <div className="tl-right">
                    <div className="tl-period">{exp.period}</div>
                    {exp.note && <div className="tl-note">{exp.note}</div>}
                  </div>
                </div>
                <ul className="tl-points">
                  {exp.points.map((p, j) => <li key={j}>{p}</li>)}
                </ul>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   PROJECT MODAL
───────────────────────────────────────────── */
function Modal({ project, onClose }) {
  useEffect(() => {
    const esc = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', esc);
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', esc); document.body.style.overflow = ''; };
  }, [onClose]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>✕</button>
        <div className="modal-tag" style={{ color: project.color }}>{project.tag}</div>
        <h2 className="modal-title" style={{ borderColor: project.color }}>{project.title}</h2>
        <div className="modal-body">
          {project.detail.split('\n').map((line, i) =>
            line.startsWith('•') ? (
              <div key={i} className="modal-bullet"><span style={{ color: project.color }}>▸</span> {line.slice(1).trim()}</div>
            ) : line.trim() === '' ? (
              <div key={i} className="modal-spacer" />
            ) : (
              <p key={i} className="modal-para">{line}</p>
            )
          )}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   PROJECTS
───────────────────────────────────────────── */
function Projects() {
  const [active, setActive] = useState(null);
  return (
    <section id="projects" className="projects section">
      <div className="container">
        <p className="label">// Selected Projects</p>
        <h2 className="section-title">Things I've<br /><em>built.</em></h2>
        <div className="projects-grid">
          {PROJECTS.map(p => (
            <div
              className="proj-card"
              key={p.id}
              onClick={() => setActive(p)}
              style={{ '--pc': p.color }}
            >
              <div className="proj-id" style={{ color: p.color }}>{p.id}</div>
              <div className="proj-tag" style={{ color: p.color }}>{p.tag}</div>
              <h3 className="proj-title">{p.title}</h3>
              <p className="proj-short">{p.short}</p>
              <div className="proj-cta">Click to read more →</div>
            </div>
          ))}
        </div>
      </div>
      {active && <Modal project={active} onClose={() => setActive(null)} />}
    </section>
  );
}

/* ─────────────────────────────────────────────
   SKILLS
───────────────────────────────────────────── */
function Skills() {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVis(true); }, { threshold: 0.2 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section id="skills" className="skills-sec section" ref={ref}>
      <div className="container">
        <p className="label">// Technical Skills</p>
        <h2 className="section-title">My<br /><em>toolkit.</em></h2>
        <div className="skills-grid">
          {SKILLS.map(({ cat, items }) => (
            <div className="skill-group" key={cat}>
              <div className="skill-cat">{cat}</div>
              <div className="skill-tags">
                {items.map((item, i) => (
                  <span
                    key={item}
                    className={`skill-tag ${vis ? 'visible' : ''}`}
                    style={{ transitionDelay: `${i * 0.05}s` }}
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="pub-box">
          <p className="label">// Publication</p>
          <p className="pub-text">
            <span className="pub-authors">Koli Puru, Silawat Sameer, Gaikar Kalpak, Salunkhe Atharv, Prof. Paramjit Thakur</span>
            {' — '}
            <em>"Fabrication of Biofilter and Piping in Aquaponics Farming"</em>
            {' — '}
            International Journal of Industrial Engineering and Design, Vol. 8, Issue 1, Jul 2022
          </p>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   CONTACT
───────────────────────────────────────────── */
function Contact() {
  return (
    <section id="contact" className="contact section">
      <div className="container">
        <p className="label">// Hire Me</p>
        <h2 className="section-title">Let's build<br /><em>together.</em></h2>
        <p className="contact-intro">
          Open to AI Engineer roles, consulting, and collaborations. Reach out directly — I respond fast.
        </p>
        <div className="contact-cards">
          <a href="tel:+91 7021150676" className="contact-card">
            <div className="cc-icon">📞</div>
            <div className="cc-label">Call Me</div>
            <div className="cc-value">+91 — 7021150676</div>
            <div className="cc-hint">Direct call · fastest response</div>
          </a>
          <a href="mailto:purukoli437@gmail.com" className="contact-card">
            <div className="cc-icon">✉️</div>
            <div className="cc-label">Email</div>
            <div className="cc-value">purukoli437@gmail.com</div>
            <div className="cc-hint">For detailed enquiries</div>
          </a>
          <a href="https://www.linkedin.com/in/puru-koli-3073861a4?utm_source=share_via&utm_content=profile&utm_medium=member_android" target="_blank" rel="noreferrer" className="contact-card">
            <div className="cc-icon">🔗</div>
            <div className="cc-label">LinkedIn</div>
            <div className="cc-value">linkedin.com/in/puru-koli</div>
            <div className="cc-hint">Connect professionally</div>
          </a>
        </div>
        <div className="availability">
          <span className="avail-dot" />
          Available for new opportunities
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   FOOTER
───────────────────────────────────────────── */
function Footer() {
  return (
    <footer className="footer">
      <div className="container">
        <span className="footer-mono">puru.koli © {new Date().getFullYear()}</span>
      </div>
    </footer>
  );
}

/* ─────────────────────────────────────────────
   APP
───────────────────────────────────────────── */
export default function App() {
  return (
    <>
      <Navbar />
      <Hero />
      <About />
      <Experience />
      <Projects />
      <Skills />
      <Contact />
      <Footer />
    </>
  );
}
